import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./DashboardPage.css";
import "./DashboardHome.css";

function DashboardPage() {
  const navigate = useNavigate();
  const session = JSON.parse(localStorage.getItem("userSession") || "null");
  const hasLoanTaken = session?.hasLoanTaken === true;

  const [user, setUser] = useState({
    name: "Loading...",
    kycStatus: "pending",
  });
  const [stats, setStats] = useState({
    creditScore: null,
    availableCredit: "5,00,000",
    activeLoans: 0,
    pendingLoans: 0,
    upcomingPayment: "0",
  });
  const [creditStatus, setCreditStatus] = useState(null);
  const [isCrifLoading, setIsCrifLoading] = useState(
    () => !sessionStorage.getItem("crifReport"),
  );
  const [crifError, setCrifError] = useState(false);
  const [gamification, setGamification] = useState({
    pointsBalance: 0,
    totalXp: 0,
    currentLevel: 1,
    currentStage: 1,
  });
  const [activities, setActivities] = useState([]);
  const [canApply, setCanApply] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const API_URL = process.env.REACT_APP_API_URL || "";

  const fetchDashboardData = useCallback(async () => {
    const session = JSON.parse(localStorage.getItem("userSession") || "null");
    if (!session?.userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/dashboard/all-data/${session.userId}`,
        {
          headers: session.token
            ? { Authorization: `Bearer ${session.token}` }
            : undefined,
        },
      );
      const data = await res.json();
      if (data.success) {
        setUser({
          id: session.userId,
          name: data.user.name,
          kycStatus: data.user.kycStatus || "pending",
        });
        setStats(data.stats);
        setCanApply(data.applicationEligibility?.canApply === true);
        if (data.gamification)
          setGamification((prev) => ({ ...prev, ...data.gamification }));
        setActivities(data.activities || []);
      }
    } catch (_) {
    } finally {
      setIsLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const applyCrifReport = (report) => {
      if (!report || typeof report !== "object") return;

      setIsCrifLoading(false);
      setCrifError(false);
      const score = report.creditScore;
      setCreditStatus(
        report.eligibilityStatus ||
          (score === null || score === undefined || score === ""
            ? "NEW_TO_CREDIT"
            : null),
      );
      setStats((prev) => ({
        ...prev,
        creditScore:
          score === null || score === undefined || score === ""
            ? null
            : Number(score),
      }));
    };

    try {
      const cachedReport = JSON.parse(
        sessionStorage.getItem("crifReport") || "null",
      );
      applyCrifReport(cachedReport);
    } catch {
      sessionStorage.removeItem("crifReport");
    }

    const handleCrifReportUpdated = (event) => applyCrifReport(event.detail);
    const handleCrifReportLoading = () => {
      setIsCrifLoading(true);
      setCrifError(false);
    };
    const handleCrifReportFailed = () => {
      setIsCrifLoading(false);
      setCrifError(true);
    };
    window.addEventListener("crifReportUpdated", handleCrifReportUpdated);
    window.addEventListener("crifReportLoading", handleCrifReportLoading);
    window.addEventListener("crifReportFailed", handleCrifReportFailed);

    return () => {
      window.removeEventListener("crifReportUpdated", handleCrifReportUpdated);
      window.removeEventListener("crifReportLoading", handleCrifReportLoading);
      window.removeEventListener("crifReportFailed", handleCrifReportFailed);
    };
  }, []);

  const displayName =
    user && user.name && user.name !== "Loading..."
      ? String(user.name)
          .split(" ")
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")
      : "User";

  const getScoreInfo = (score, status) => {
    if (status === "NEW_TO_CREDIT")
      return { label: "New to Credit", color: "#64748b", track: "#e0e9ff" };
    if (
      score === null ||
      score === undefined ||
      !Number.isFinite(Number(score))
    )
      return { label: "Checking", color: "#64748b", track: "#e0e9ff" };
    if (score >= 750)
      return { label: "Excellent", color: "#2563eb", track: "#dbeafe" };
    if (score >= 650)
      return { label: "Good", color: "#9a6b24", track: "#f3ead7" };
    return { label: "Fair", color: "#b25d4c", track: "#f5e3df" };
  };

  console.log("DashboardPage render", {
    user,
    stats,
    creditStatus,
    isCrifLoading,
    crifError,
    gamification,
    activities,
    isLoading,
  });
  const scoreInfo = getScoreInfo(stats.creditScore, creditStatus);
  const gaugeR = 54;
  const gaugeC = 2 * Math.PI * gaugeR;
  const gaugeArc = gaugeC * 0.75;
  const gaugeFill =
    (Math.min(100, ((stats.creditScore || 0) / 900) * 100) / 100) * gaugeArc;

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return { icon: "☀️", text: "Good morning" };
    if (h < 17) return { icon: "🌤️", text: "Good afternoon" };
    return { icon: "🌙", text: "Good evening" };
  };

  const quickActions = [
    {
      icon: "🚀",
      label: "Apply for Loan",
      desc: "Start your journey",
      path: "/dashboard/apply-loan",
      highlight: true,
    },
    {
      icon: "💰",
      label: "My Loans",
      desc: "View all loans",
      path: "/dashboard/loans",
    },
    {
      icon: "💳",
      label: "Payments",
      desc: "Pay EMI & dues",
      path: "/dashboard/payments",
    },
    {
      icon: "📄",
      label: "Documents",
      desc: "Upload & manage",
      path: "/dashboard/documents",
    },
    {
      icon: "🆔",
      label: "KYC Verify",
      desc: "Complete identity",
      path: "/dashboard/kyc",
    },
    {
      icon: "💬",
      label: "Support",
      desc: "Get help",
      path: "/dashboard/support",
    },
  ].filter(
    (action) =>
      (action.label !== "Apply for Loan" || canApply) &&
      (hasLoanTaken || action.label !== "Payments"),
  );

  const normalizedKyc = String(user.kycStatus || "").toLowerCase();
  const hasKycCompleted = ["completed", "verified", "under_review"].includes(normalizedKyc);
  const hasLoanApplied =
    Number(stats.activeLoans || 0) > 0 || Number(stats.pendingLoans || 0) > 0;
  const hasRepaymentActivity = (activities || []).some((a) => {
    const type = String(a?.activity_type || "").toLowerCase();
    const desc = String(a?.description || "").toLowerCase();
    return (
      type.includes("payment") ||
      desc.includes("payment") ||
      desc.includes("emi")
    );
  });

  const journeySteps = [
    { step: 1, label: "Sign Up", done: Boolean(user.id) },
    { step: 2, label: "KYC Verified", done: hasKycCompleted },
    { step: 3, label: "Loan Applied", done: hasLoanApplied },
    { step: 4, label: "First Repayment", done: hasRepaymentActivity },
  ];

  const greeting = getGreeting();
  const firstPendingIndex = journeySteps.findIndex((s) => !s.done);
  const stepStatus = (s, i) =>
    s.done ? "done" : i === firstPendingIndex ? "current" : "pending";
  const stepStatusLabel = { done: "Completed", current: "In Progress", pending: "Pending" };
  const hasDues = Number(String(stats.upcomingPayment).replace(/,/g, "")) > 0;
  const primaryActions = quickActions.slice(0, 2);
  const secondaryActions = quickActions.slice(2);
  const scoreText = isCrifLoading
    ? "…"
    : creditStatus === "NEW_TO_CREDIT"
    ? "NTC"
    : stats.creditScore !== null && stats.creditScore !== undefined
    ? stats.creditScore
    : "--";
  const scoreLabel = isCrifLoading
    ? "Checking"
    : crifError && stats.creditScore == null
      ? "Unavailable"
      : scoreInfo.label;

  return (
    <div className="kd" aria-busy={isLoading || isCrifLoading}>
      <div className="kd-bg" aria-hidden="true">
        <svg className="kd-bg-line kd-bg-line-left" viewBox="0 0 240 90">
          <polyline points="0,80 40,60 70,68 110,38 150,50 200,18 230,8" />
          <circle cx="230" cy="8" r="4" />
        </svg>
        <svg className="kd-bg-line kd-bg-line-right" viewBox="0 0 240 90">
          <polyline points="0,85 40,70 80,74 120,48 160,56 200,24 235,6" />
          <circle cx="235" cy="6" r="4" />
        </svg>
        <span className="kd-bg-chip kd-bg-chip-1">₹</span>
        <span className="kd-bg-chip kd-bg-chip-2">%</span>
      </div>

      {isLoading && (
        <div className="kdash-refreshing" role="status">
          <div className="kdash-spinner kdash-spinner-small" />
          <span>Loading your latest account details…</span>
        </div>
      )}

      {/* ── Welcome Hero ── */}
      <section className="kd-hero">
        <div className="kd-hero-copy">
          <p className="kd-hero-greeting">
            <span aria-hidden="true">{greeting.icon}</span> {greeting.text},
          </p>
          <h1 className="kd-hero-name">
            Welcome back,{" "}
            <span>{displayName}</span>
          </h1>
          <p className="kd-hero-sub">
            Everything about your loan journey, clearly in one place.
          </p>
        </div>

        <div className="kd-hero-art" aria-hidden="true">
          {/* <div className="kd-art-cards">
            <span />
            <span />
          </div>
          <div className="kd-art-checklist">
            <strong>Your Loan Journey</strong>
            {journeySteps.map((s) => (
              <span key={s.step} className={s.done ? "is-done" : ""}>
                <i>{s.done ? "✓" : ""}</i>
                {s.label}
              </span>
            ))}
          </div>
          <span className="kd-art-chip kd-art-chip-rupee">₹</span>
          <span className="kd-art-chip kd-art-chip-pct">%</span>
          <span className="kd-art-chip kd-art-chip-bars">
            <b />
            <b />
            <b />
          </span> */}
        </div>

        <div className="kd-hero-gauge">
          <svg viewBox="0 0 130 130" className="kd-gauge-svg">
            <circle
              cx="65"
              cy="65"
              r={gaugeR}
              fill="none"
              stroke="#e0e9ff"
              strokeWidth="11"
              strokeDasharray={`${gaugeArc} ${gaugeC}`}
              strokeLinecap="round"
              transform="rotate(135 65 65)"
            />
            <defs>
              <linearGradient id="kdGaugeGrad" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#2563eb" />
              </linearGradient>
            </defs>
            <circle
              cx="65"
              cy="65"
              r={gaugeR}
              fill="none"
              stroke="url(#kdGaugeGrad)"
              strokeWidth="11"
              strokeDasharray={`${gaugeFill} ${gaugeC}`}
              strokeLinecap="round"
              transform="rotate(135 65 65)"
              className="kdash-gauge-fill"
            />
          </svg>
          <div className="kd-gauge-center">
            <span className="kd-gauge-value">{scoreText}</span>
            <span className="kd-gauge-label" style={{ color: scoreInfo.color }}>
              {scoreLabel}
            </span>
          </div>
          <p className="kd-gauge-caption" aria-live="polite">
            {isCrifLoading
              ? "Fetching CRIF report…"
              : crifError && stats.creditScore == null
                ? "Credit report unavailable"
                : "Credit Score"}
          </p>
        </div>
      </section>

      {/* ── Reassurance ── */}
      <section className="kd-trust" aria-label="Account reassurance">
        <div className="kd-trust-item">
          <span className="kd-trust-icon tone-green" aria-hidden="true">🛡️</span>
          <div>
            <strong>Protected</strong>
            <small>Bank-Grade Security</small>
          </div>
        </div>
        <div className="kd-trust-item">
          <span className="kd-trust-icon tone-blue" aria-hidden="true">👁️</span>
          <div>
            <strong>Transparent</strong>
            <small>No Hidden Steps</small>
          </div>
        </div>
        <div className="kd-trust-item">
          <span className="kd-trust-icon tone-indigo" aria-hidden="true">🎧</span>
          <div>
            <strong>Supported</strong>
            <small>Help Whenever You Need It</small>
          </div>
        </div>
      </section>

      {/* ── Payment + Loan overview ── */}
      <section className="kd-row kd-row-2">
        <div className="kd-card kd-pay">
          <div className="kd-card-head">
            <span className="kd-card-icon" aria-hidden="true">💳</span>
            <div className="kd-card-title">
              <h2>Next Payment</h2>
              <p>Stay on track with your repayments</p>
            </div>
            <span className={`kd-chip ${hasDues ? "kd-chip-red" : "kd-chip-green"}`}>
              <i aria-hidden="true" />
              {hasDues ? "Due soon" : "No dues"}
            </span>
          </div>
          <div className="kd-pay-body">
            <div>
              <span className="kd-pay-value">₹{stats.upcomingPayment}</span>
              <span className="kd-pay-note">
                {hasDues ? "Upcoming EMI due" : "No dues at the moment"}
              </span>
            </div>
            <div className="kd-pay-art" aria-hidden="true">
              <span />
            </div>
          </div>
        </div>

        <div className="kd-card kd-overview">
          <div className="kd-card-head">
            <span className="kd-card-icon tone-blue" aria-hidden="true">📊</span>
            <div className="kd-card-title">
              <h2>Loan Overview</h2>
              <p>Your current application status</p>
            </div>
            <button
              type="button"
              className="kd-link-btn"
              onClick={() => navigate("/dashboard/loans")}
            >
              View All →
            </button>
          </div>
          <ol className="kd-track">
            {journeySteps.map((s, i) => {
              const status = stepStatus(s, i);
              return (
                <li key={s.step} className={`kd-track-step is-${status}`}>
                  <span className="kd-track-dot">{s.done ? "✓" : s.step}</span>
                  <strong>{s.label}</strong>
                  <small>{stepStatusLabel[status]}</small>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ── CTA + Journey + Quick Actions ── */}
      <section className="kd-row kd-row-3">
        <div className="kd-cta">
          <div className="kd-cta-art" aria-hidden="true">
            {/* <span className="kd-cta-doc" />
            <span className="kd-cta-chip kd-cta-chip-1">₹</span>
            <span className="kd-cta-chip kd-cta-chip-2">📈</span>
            <span className="kd-cta-chip kd-cta-chip-3">⭐</span> */}
          </div>
          <div className="kd-cta-copy">
            <span className="kd-cta-rocket" aria-hidden="true">🚀</span>
            {stats.pendingLoans > 0 ? (
              <>
                <span className="kd-cta-badge">⏳ Under Review</span>
                <h2>Loan Pending Approval</h2>
                <p>
                  Your loan application is currently under review by our
                  underwriting team. We will notify you once it is approved.
                </p>
                <button
                  className="kd-cta-btn"
                  onClick={() => navigate("/dashboard/loans")}
                >
                  View Status →
                </button>
              </>
            ) : stats.activeLoans > 0 ? (
              <>
                <span className="kd-cta-badge">✅ Active Loan</span>
                <h2>You have an active loan</h2>
                <p>
                  You currently have an ongoing loan. You can apply for a new
                  one after completing your repayments.
                </p>
                <button
                  className="kd-cta-btn"
                  onClick={() => navigate("/dashboard/loans")}
                >
                  View My Loan →
                </button>
              </>
            ) : (
              <>
                <span className="kd-cta-badge">⚡ Get Started</span>
                <h2>Ready when you are</h2>
                <p>
                  Check your eligibility first, then continue at your pace.
                  Your progress stays saved along the way.
                </p>
                <button
                  className="kd-cta-btn"
                  disabled={!canApply}
                  onClick={() => navigate("/dashboard/apply-loan")}
                >
                  {canApply ? "Apply for Loan →" : "Application unavailable"}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="kd-card kd-journey">
          <div className="kd-card-head kd-card-head-line">
            <span className="kd-card-icon kd-card-icon-sm tone-blue" aria-hidden="true">🗓️</span>
            <div className="kd-card-title">
              <h2>Your Journey</h2>
            </div>
          </div>
          <ol className="kd-journey-list">
            {journeySteps.map((s, i) => {
              const status = stepStatus(s, i);
              return (
                <li key={s.step} className={`kd-journey-item is-${status}`}>
                  <span className="kd-journey-dot">{s.done ? "✓" : s.step}</span>
                  <div>
                    <strong>{s.label}</strong>
                    <small>{stepStatusLabel[status]}</small>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="kd-card kd-actions">
          <div className="kd-card-head kd-card-head-line">
            <span className="kd-card-icon kd-card-icon-sm tone-amber" aria-hidden="true">⚡</span>
            <div className="kd-card-title">
              <h2>Quick Actions</h2>
            </div>
          </div>
          <div className="kd-actions-primary">
            {primaryActions.map((a) => (
              <button
                key={a.path}
                className={`kd-action kd-action-lg ${a.highlight ? "is-highlight" : ""}`}
                onClick={() => navigate(a.path)}
              >
                <span className="kd-action-icon" aria-hidden="true">{a.icon}</span>
                <span className="kd-action-text">
                  <span className="kd-action-label">{a.label}</span>
                  <span className="kd-action-desc">{a.desc}</span>
                </span>
                <span className="kd-action-arrow" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
          {secondaryActions.length > 0 && (
            <div className="kd-actions-secondary">
              {secondaryActions.map((a) => (
                <button
                  key={a.path}
                  className="kd-action kd-action-sm"
                  onClick={() => navigate(a.path)}
                >
                  <span className="kd-action-icon" aria-hidden="true">{a.icon}</span>
                  <span className="kd-action-text">
                    <span className="kd-action-label">{a.label}</span>
                    <span className="kd-action-desc">{a.desc}</span>
                  </span>
                  <span className="kd-action-arrow" aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default DashboardPage;
